import {css, useTheme} from '@mui/material';
import React from 'react';

import {DlMuiDynamicIcon} from './DlMuiDynamicIcon';
import {GetStylesFunction} from '../common';

type IconColors = {
  fill?: string;
  background?: string;
};
type StateToColors = Record<'default' | 'hover' | 'selected' | 'selectedHover' | 'active' | 'disabled', IconColors>;
export type LightIconButtonProps = {
  iconName: string;
  useFixedViewBox?: string | number;
  title?: string;
  selected?: boolean;
  disabled?: boolean;
  colors?: Partial<StateToColors>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  wrapperStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  dataName?: string;
  noButtonStyles?: boolean;
  preserveAspectRatio?: string;
};
export const LightIconButton: React.FC<LightIconButtonProps> = (props) => {
  const {
    iconName = 'filterIcon',
    useFixedViewBox,
    title,
    selected,
    disabled,
    colors,
    onClick,
    wrapperStyle,
    style,
    dataName = 'light-icon-button',
    ...extraProps
  } = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme, params: {colors}});
  return (
    <DlMuiDynamicIcon
      data-name={dataName}
      {...extraProps}
      data-selected={selected}
      onClick={onClick}
      tabIndex={disabled ? -1 : 0}
      disabled={disabled}
      inputCss={muiStyles.iconButton}
      svgIconInputCss={muiStyles.icon}
      iconName={iconName}
      useFixedViewBox={useFixedViewBox}
      iconTitle={title}
      style={wrapperStyle}
      svgIconStyle={style}
    />
  );
};
const getStyles: GetStylesFunction = (options) => {
  const colors = options?.params?.colors as StateToColors | undefined;
  const defaultFill = colors?.default?.fill ?? 'var(--dlLightIconButtonFill)';
  const defaultBackground = colors?.default?.background ?? 'var(--dlLightIconButtonBackground)';
  const hoverFill = colors?.hover?.fill ?? 'var(--dlLightIconButtonHoverFill)';
  const hoverBackground = colors?.hover?.background ?? 'var(--dlLightIconButtonHoverBackground)';
  const selectedFill = colors?.selected?.fill ?? 'var(--dlLightIconButtonSelectedFill)';
  const selectedBackground = colors?.selected?.background ?? 'var(--dlLightIconButtonSelectedBackground)';
  const selectedHoverFill = colors?.selectedHover?.fill ?? 'var(--dlLightIconButtonSelectedHoverFill)';
  const selectedHoverBackground = colors?.selectedHover?.background ?? 'var(--dlLightIconButtonSelectedHoverBackground)';
  const activeFill = colors?.active?.fill ?? 'var(--dlLightIconButtonActiveFill)';
  const activeBackground = colors?.active?.background ?? 'var(--dlLightIconButtonActiveBackground)';
  const disabledFill = colors?.disabled?.fill ?? 'var(--dlLightIconButtonDisabledFill)';
  const disabledBackground = colors?.disabled?.background ?? 'var(--dlLightIconButtonDisabledBackground)';
  return {
    iconButton: css`
      border: none;
      border-radius: 4px;
      width: 32px;
      height: 32px;
      padding: 8px;
      color: var(--fill) !important;
      background: var(--background) !important;
      --fill: ${defaultFill};
      --background: ${defaultBackground};
      &:hover {
        --fill: ${hoverFill};
        --background: ${hoverBackground};
      }
      &[data-selected='true'] {
        --fill: ${selectedFill};
        --background: ${selectedBackground};
        &:hover {
          --fill: ${selectedHoverFill};
          --background: ${selectedHoverBackground};
        }
      }
      &:active {
        --fill: ${activeFill} !important;
        --background: ${activeBackground} !important;
      }
      &[disabled] {
        --fill: ${disabledFill} !important;
        --background: ${disabledBackground} !important;
      }
    `,
    icon: css`
      width: 100%;
      height: 100%;
    `,
  };
};
